/// Man-overboard recovery manoeuvres. Each entry includes a
/// numbered step list (what the helmsman does) plus a series of
/// path waypoints so the renderer can animate the boat back to the
/// casualty.
///
/// Coordinate convention matches IRPCS scenarios: top-down view on
/// a 100 × 100 viewbox with NORTH up. Heading 0° = up, increasing
/// clockwise. The MOB casualty is the fixed target; the boat
/// starts from `start` and traces the listed waypoints in order.

export type VesselType = "sail" | "power" | "any";

export type SailPoint =
  | "close-hauled"
  | "close-reach"
  | "beam-reach"
  | "broad-reach"
  | "run"
  | "luffing"
  | "n/a";

export interface Waypoint {
  /// Position on the 100×100 viewbox.
  x: number;
  y: number;
  /// Heading at this point (degrees, 0 = north).
  heading: number;
  /// Optional sailing point — drives the sail-icon orientation in
  /// the renderer. `"n/a"` for power-only manoeuvres.
  sail?: SailPoint;
  /// Short caption describing what the helmsman does at this point.
  caption: string;
}

export interface MobManoeuvre {
  id: string;
  name: string;
  /// Power, sail, or any.
  vessel: VesselType;
  /// One-line summary.
  summary: string;
  /// When this manoeuvre is the right choice.
  bestFor: string;
  /// Numbered helmsman actions, used in the reference panel below
  /// the animation.
  steps: readonly string[];
  /// Fixed casualty position.
  mob: { x: number; y: number };
  /// Boat path. The first waypoint is the starting position (when
  /// the MOB is sighted); subsequent waypoints define the recovery
  /// track. Final waypoint should bring the boat to / past the
  /// casualty.
  path: readonly Waypoint[];
  /// Animation duration for one full pass, in ms.
  durationMs: number;
}

export const MANOEUVRES: Readonly<Record<string, MobManoeuvre>> = Object.freeze({
  figure_eight: {
    id: "figure_eight",
    name: "Figure-of-eight (sailing)",
    vessel: "sail",
    summary:
      "The standard RYA sail-boat recovery. Sail a beam reach away, tack, sail back close-hauled to a position upwind of the casualty, then drop the sails or luff up to stop alongside.",
    bestFor:
      "Sailing — when you have time and sea room. Predictable, controllable, and you arrive head-to-wind for the pick-up.",
    steps: [
      "Shout 'MAN OVERBOARD!' — keep eyes on the casualty.",
      "Push the helm down and bear off to a beam reach (90° off the wind).",
      "Sail away on the beam reach for ~5 boat lengths to gain sea room.",
      "Tack onto the opposite tack (now heading roughly back toward the casualty).",
      "Sail close-hauled until the casualty is on your lee bow.",
      "Bear off to a close-reach, easing sheets, approaching slowly.",
      "Final approach: luff up alongside the casualty, head-to-wind, stopped — pick up to leeward.",
    ],
    mob: { x: 25, y: 50 },
    path: [
      { x: 25, y: 50, heading: 90, sail: "close-hauled", caption: "MOB sighted abeam." },
      { x: 60, y: 65, heading: 110, sail: "beam-reach", caption: "Bear off to a beam reach." },
      { x: 80, y: 78, heading: 110, sail: "beam-reach", caption: "Sail clear for ~5 boat lengths." },
      { x: 80, y: 60, heading: 300, sail: "close-hauled", caption: "Tack — now on the opposite tack." },
      { x: 55, y: 35, heading: 290, sail: "close-hauled", caption: "Close-hauled back toward the casualty." },
      { x: 35, y: 40, heading: 270, sail: "close-reach", caption: "Ease sheets, bear off to a close reach." },
      { x: 28, y: 48, heading: 0, sail: "luffing", caption: "Final approach — luff head-to-wind alongside." },
    ],
    durationMs: 7000,
  },
  beam_reach_return: {
    id: "beam_reach_return",
    name: "Beam-reach return (sailing)",
    vessel: "sail",
    summary:
      "The fastest sail recovery. Bear off immediately to a beam reach, count the seconds, then tack and return on a beam reach with the wind on the opposite side. Predictable bearing back to the casualty.",
    bestFor:
      "Sailing — when you must turn around quickly. Less precise than figure-of-eight but very fast.",
    steps: [
      "Shout 'MAN OVERBOARD!' — designate a spotter.",
      "Immediately bear off to a beam reach. Note the time (count out loud — 'one… two… three…').",
      "Sail on the beam reach for whatever the helmsman judges sufficient (typically 15–30 s).",
      "Tack. Steer onto a beam reach with the wind on the OPPOSITE side from the outbound leg.",
      "Sail back along the reciprocal until the casualty appears.",
      "Final approach: luff up alongside head-to-wind.",
    ],
    mob: { x: 50, y: 50 },
    path: [
      { x: 50, y: 50, heading: 0, sail: "close-hauled", caption: "MOB sighted ahead." },
      { x: 75, y: 65, heading: 130, sail: "beam-reach", caption: "Bear off — beam reach away." },
      { x: 88, y: 78, heading: 130, sail: "beam-reach", caption: "Hold the reach — count seconds." },
      { x: 85, y: 60, heading: 310, sail: "close-hauled", caption: "Tack onto opposite tack." },
      { x: 60, y: 50, heading: 230, sail: "beam-reach", caption: "Beam reach back — wind on other side." },
      { x: 52, y: 50, heading: 180, sail: "luffing", caption: "Casualty ahead — luff to stop." },
    ],
    durationMs: 6500,
  },
  williamson: {
    id: "williamson",
    name: "Williamson turn (power)",
    vessel: "power",
    summary:
      "Standard ship's recovery — full helm one way, then full helm the other to retrace the outbound track. Brings the vessel back along the exact reciprocal even when the casualty was lost from sight (works in fog or at night).",
    bestFor:
      "Power vessel, especially when the casualty was lost from sight or vessel speed is high. Mandatory on large ships per IMO.",
    steps: [
      "Shout 'MAN OVERBOARD!' — sound 5 short blasts (Rule 34(d)) if other traffic nearby. Throw a danbuoy + lifering immediately.",
      "Apply full helm in the direction of the casualty's side (e.g. MOB to starboard → full right rudder).",
      "When the heading has changed by 60° from the original course, shift to FULL OPPOSITE helm.",
      "Continue the turn until the heading is on the reciprocal (180° from the original course).",
      "Steady up and reduce speed. The vessel is now retracing the outbound track.",
      "Steer toward the casualty (or danbuoy). Stop windward of the casualty so the boat drifts down onto them.",
    ],
    mob: { x: 30, y: 60 },
    path: [
      { x: 30, y: 60, heading: 0, sail: "n/a", caption: "MOB! Throw lifering. Note position." },
      { x: 38, y: 35, heading: 30, sail: "n/a", caption: "Full right rudder." },
      { x: 55, y: 25, heading: 60, sail: "n/a", caption: "Heading changed 60° — shift full LEFT rudder." },
      { x: 70, y: 30, heading: 280, sail: "n/a", caption: "Continue turn through opposite helm." },
      { x: 65, y: 45, heading: 200, sail: "n/a", caption: "Coming onto the reciprocal course (180°)." },
      { x: 50, y: 55, heading: 180, sail: "n/a", caption: "Steady on reciprocal — reduce speed." },
      { x: 32, y: 62, heading: 180, sail: "n/a", caption: "Stop windward of casualty." },
    ],
    durationMs: 8500,
  },
  anderson: {
    id: "anderson",
    name: "Anderson turn (power)",
    vessel: "power",
    summary:
      "Fastest recovery for a power vessel in clear visibility. Full helm toward the casualty for ~250° of turn, then steady up on a direct course back to them.",
    bestFor:
      "Power vessel, casualty in sight, clear visibility, good manoeuvrability. Quickest return time of any standard manoeuvre.",
    steps: [
      "Shout 'MAN OVERBOARD!' — throw lifering. Designate spotter.",
      "Apply FULL helm in the direction of the casualty's side.",
      "Hold full rudder through ~250° of turn — about 2/3 of a complete circle.",
      "Just before the casualty is dead ahead, ease the helm and steady on a course directly toward them.",
      "Reduce speed and approach with the casualty just upwind of the bow.",
      "Stop windward of the casualty — drift down for the pick-up.",
    ],
    mob: { x: 40, y: 50 },
    path: [
      { x: 40, y: 50, heading: 0, sail: "n/a", caption: "MOB sighted — throw lifering." },
      { x: 50, y: 30, heading: 60, sail: "n/a", caption: "Full right rudder." },
      { x: 70, y: 30, heading: 130, sail: "n/a", caption: "Continue full helm." },
      { x: 75, y: 50, heading: 200, sail: "n/a", caption: "Past 180° of turn." },
      { x: 70, y: 65, heading: 260, sail: "n/a", caption: "Coming through ~250°." },
      { x: 55, y: 62, heading: 290, sail: "n/a", caption: "Ease helm — steady on course to MOB." },
      { x: 42, y: 52, heading: 270, sail: "n/a", caption: "Stop windward of casualty." },
    ],
    durationMs: 8000,
  },
  scharnow: {
    id: "scharnow",
    name: "Scharnow turn (power)",
    vessel: "power",
    summary:
      "Like Williamson but more efficient — used when the casualty was sighted some time before the turn started. Retraces the outbound track but with a tighter loop.",
    bestFor:
      "Power vessel when the casualty is not directly behind (some distance has been run since the incident). Less precise than Williamson but covers less water.",
    steps: [
      "Shout 'MAN OVERBOARD!' — throw lifering, mark position electronically if possible.",
      "Apply FULL helm in the direction of the casualty's side.",
      "Hold full helm until the heading has changed by 240° from the original course.",
      "Shift to FULL OPPOSITE helm.",
      "Continue turning until 20° before the reciprocal course, then ease the helm to steady on the reciprocal.",
      "Track back to the casualty position and stop windward.",
    ],
    mob: { x: 25, y: 60 },
    path: [
      { x: 25, y: 60, heading: 0, sail: "n/a", caption: "MOB! Note position; this manoeuvre takes time." },
      { x: 35, y: 35, heading: 45, sail: "n/a", caption: "Full right rudder." },
      { x: 60, y: 28, heading: 130, sail: "n/a", caption: "Continue full helm." },
      { x: 75, y: 45, heading: 220, sail: "n/a", caption: "Heading changed 240° — shift full LEFT." },
      { x: 60, y: 60, heading: 200, sail: "n/a", caption: "Coming onto the reciprocal." },
      { x: 40, y: 65, heading: 180, sail: "n/a", caption: "Steady on reciprocal — track back." },
      { x: 27, y: 62, heading: 180, sail: "n/a", caption: "Stop windward of casualty." },
    ],
    durationMs: 8500,
  },
  quick_stop: {
    id: "quick_stop",
    name: "Quick-stop / crash tack (sailing)",
    vessel: "sail",
    summary:
      "Instant emergency tack — keeps the boat close to the casualty without bothering to change sails. The genoa goes aback, the boat slows hard. Drift down onto the casualty with sails luffing.",
    bestFor:
      "Sailing — when the casualty MUST stay in sight and there's no time for a controlled manoeuvre. Particularly useful short-handed.",
    steps: [
      "Shout 'MAN OVERBOARD!' — keep eyes on the casualty.",
      "Push the helm down — tack IMMEDIATELY without releasing the jib sheet.",
      "Genoa goes aback (now backwinded). Boat heels slightly and slows quickly.",
      "Hold the helm hard down — boat continues turning through the wind.",
      "Once head-to-wind / running back: control speed with mainsheet trim.",
      "Approach the casualty from leeward, sails luffing, and drift onto them.",
    ],
    mob: { x: 35, y: 60 },
    path: [
      { x: 35, y: 60, heading: 0, sail: "close-hauled", caption: "MOB sighted abeam." },
      { x: 40, y: 40, heading: 30, sail: "close-hauled", caption: "Helm down — tack without releasing jib." },
      { x: 50, y: 30, heading: 100, sail: "luffing", caption: "Genoa aback — boat slows fast." },
      { x: 60, y: 40, heading: 160, sail: "luffing", caption: "Continue turning through the wind." },
      { x: 55, y: 55, heading: 220, sail: "luffing", caption: "Reaching/running back toward MOB." },
      { x: 40, y: 62, heading: 240, sail: "luffing", caption: "Approach from leeward — drift on." },
    ],
    durationMs: 6500,
  },
});

export const MANOEUVRE_IDS: readonly string[] = Object.keys(MANOEUVRES);
